import { useMutation } from '@tanstack/react-query'
import { useAuthClient } from '../auth-context.tsx'
import { useSession } from '../session/useSession.ts'

/**
 * The one banner the shell shows above everything, most urgent first:
 * someone is acting as you; the account is read-only; a payment failed;
 * the trial ends soon. Copy and urgency are decided here so both platforms
 * say the same thing.
 */
export interface AccountNotice {
  tone: 'danger' | 'warning' | 'info'
  message: string
  action?: { label: string; to: string } | { label: string; stopImpersonating: true }
}

export function useAccountNotice(): AccountNotice | null {
  const { me } = useSession()
  if (!me) return null
  if (me.impersonatedBy)
    return {
      tone: 'danger',
      message: `A platform operator is signed in as ${me.user.name}. Everything done here is recorded in the audit log.`,
      action: { label: 'Stop', stopImpersonating: true },
    }
  const e = me.entitlements
  if (e?.readOnly)
    return {
      tone: 'danger',
      message:
        e.reason === 'trial_ended'
          ? 'Your free trial has ended. You can look at everything — and check-in still works — but nothing can be changed until you choose a plan.'
          : e.reason === 'payment_failed'
            ? 'Payment failed more than two weeks ago. The church is read-only (check-in still works) until it goes through.'
            : 'Your plan was cancelled. The church is read-only; check-in still works.',
      action: me.grants.permissions.includes('org:admin')
        ? { label: 'Billing', to: '/settings/billing' }
        : undefined,
    }
  return null
}

export function useStopImpersonating() {
  const authClient = useAuthClient()
  return useMutation({
    mutationFn: async () => {
      const { error } = await authClient.admin.stopImpersonating()
      if (error) throw new Error(error.message ?? 'Could not stop')
    },
  })
}
